/* global window */
import React, { useState, useEffect } from "react";
import cubejs from "@cubejs-client/core";
import PropTypes from "prop-types";
import { Layout, Button, Space, Form } from "antd";
import { useDispatch } from "react-redux";
import { InputItem } from "../../../components/forms/FormItems";
import CheckBoxItem from "../../../components/forms/CheckBoxItem";
import { SettingsDataSelect } from "../../../components/forms/AutoCompleteFormItem";
import PlaygroundQueryBuilder from "../../reports/PlaygroundQueryBuilder";
import { useAuthToken } from "../../../hooks";
import { CloseOutlined } from "@ant-design/icons";
import {
  setChangedFormItems,
  setVisState,
  commitChanges,
} from "../reportSlice";
import { loadPage } from "../../reports/reportsSlice";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import {
  getChangedItems,
  isVisStateChanged,
} from "../../../components/util/helpers";
import FloatingFormActions from "../../../components/forms/FloatingFormActions";

const ReportBuilder = (props) => {
  const { closeModal, activeReport, reportMode } = props;
  const dispatch = useDispatch();
  const intl = useIntl();
  const [form] = Form.useForm();
  const changedItems = useSelector(
    (state) => state.report.changedItems.changed
  );
  const changedValues = useSelector(
    (state) => state.report.changedItems.values
  );
  const createLoading = useSelector(
    (state) => state.pendingActions["report/create"]
  );
  const updateLoading = useSelector(
    (state) => state.pendingActions["report/update"]
  );
  const visState = useSelector((state) => state.report.visState);
  const [query, setQuery] = useState({});
  const [visStateChanged, setVisStateChanged] = useState(false);

  useEffect(() => {
    if (activeReport.id) {
      dispatch(setVisState({ vis: activeReport }));
      form.setFieldsValue({
        ...activeReport,
        name: reportMode === "duplicate" ? "" : activeReport.name,
        categories: activeReport.categories.map((a) => a.id),
      });
    }
    return function cleanup() {
      dispatch(setVisState({ vis: {} }));
    };
  }, [activeReport, dispatch]);

  const onFinish = async () => {
    const action = await dispatch(commitChanges({ reportMode }));
    if (!action.error) {
      if (action.type === "report/create/fulfilled") {
        closeModal();
      }
      dispatch(
        loadPage({
          current: 1,
        })
      );
    }
  };

  const token = useAuthToken();
  const cubejsApi = cubejs(process.env.REACT_APP_CUBEJS_TOKEN, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    apiUrl: `/api/v1/analytics`,
  });
  const onVizStateChanged = (v) => {
    const isChanged = isVisStateChanged(activeReport, v);
    setVisStateChanged(isChanged);
    dispatch(setVisState({ vis: v }));
  };

  const onValuesChange = (changedValues, allValues) => {
    const arr = getChangedItems(
      changedValues,
      allValues,
      changedItems,
      activeReport
    );
    dispatch(setChangedFormItems({ changed: arr, values: allValues }));
  };

  return (
    (token && cubejsApi && (
      <Layout
        style={{
          background: "transparent",
          overflow: "auto",
          height: "100%",
        }}
      >
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            padding: "20px",
          }}
        >
          <Button
            onClick={closeModal}
            size="large"
            shape="circle"
            icon={<CloseOutlined />}
          />
          <div>
            <Form
              layout="vertical"
              onFinish={onFinish}
              form={form}
              onValuesChange={onValuesChange}
            >
              <Space size="large">
                <CheckBoxItem
                  showLabel={true}
                  name="support_date_range"
                  category="reports"
                />
                <CheckBoxItem
                  showLabel={true}
                  name="show_in_branch"
                  category="reports"
                />
                <CheckBoxItem
                  showLabel={true}
                  name="support_totals"
                  category="reports"
                />
                <div style={{ flex: 1, width: "200px" }}>
                  <SettingsDataSelect
                    mode={"multiple"}
                    name="categories"
                    category="reports"
                    settingsdataName={"system_tables/report_categories"}
                  />
                </div>
                <InputItem name="name" required={true} category="reports" />
              </Space>
              <FloatingFormActions
                resource={"reports"}
                action={"editor"}
                // mode={"edit"}
                hideDelete={true}
                hideDecline={true}
                saveLoading={createLoading || updateLoading}
                saveText={intl.formatMessage({
                  id: "app.actions.saveChanges",
                })}
                show={true}
              />
            </Form>
          </div>
        </div>
        <PlaygroundQueryBuilder
          query={query}
          setQuery={(q) => setQuery(q)}
          cubejsApi={cubejsApi}
          apiUrl={`/api/v1/analytics`}
          onVizStateChanged={onVizStateChanged}
          visState={visState}
          // dashboardSource={dashboardSource}
        />
      </Layout>
    )) ||
    null
  );
};

ReportBuilder.defaultProps = {};

export default ReportBuilder;
